import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons"

import CaseStudiesSection from "../case-studies-section"
import { LOGO_PLACEMENT } from "./brand-logo-scales"

// Helper component for text sections
const TextSection = ({ html, className = "", removeInlineStyles }) => {
  if (!html) return null
  return (
    <div
      className={`case-study-text-section has-text-purple ${className}`}
      dangerouslySetInnerHTML={{ __html: removeInlineStyles(html) }}
    />
  )
}

export const CaseStudyChallengeSection = ({
  primaryText,
  secondaryText,
  challengeCards = [],
  challengeIcons = [],
  removeInlineStyles,
}) => {
  if (!primaryText && !secondaryText && !challengeCards.length) {
    return null
  }

  return (
    <div className="case-study-container">
      <div className="container py-5 mb-5">
        <h2 className="case-study-section-title has-text-weight-medium has-text-purple mb-5 mt-6">
          The Challenge
        </h2>

        <TextSection
          html={primaryText}
          className="mb-4"
          removeInlineStyles={removeInlineStyles}
        />

        {challengeCards.length > 0 && (
          <div className="case-study-challenge-cards py-6">
            {challengeCards.map((card, index) => {
              const icon = challengeIcons[index]
              return (
                <div key={card.key} className="case-study-challenge-card">
                  <div className="case-study-challenge-card-content">
                    {icon?.src && (
                      <div className="case-study-challenge-card-icon">
                        <img src={icon.src} alt={icon.alt} />
                      </div>
                    )}
                    {card.title && (
                      <h3
                        className="case-study-challenge-card-title has-text-purple"
                        dangerouslySetInnerHTML={{ __html: card.title }}
                      />
                    )}
                    {card.text && (
                      <p
                        className="case-study-challenge-card-text has-text-purple"
                        dangerouslySetInnerHTML={{ __html: card.text }}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <TextSection
          html={secondaryText}
          className="mb-5"
          removeInlineStyles={removeInlineStyles}
        />
      </div>
    </div>
  )
}

export const CaseStudySolutionSection = React.forwardRef(
  ({ primaryText, secondaryText, removeInlineStyles, children }, ref) => {
    if (!primaryText && !secondaryText && !children) {
      return null
    }

    return (
      <div ref={ref} className="case-study-solution-section">
        <div className="case-study-container">
          <div className="container mt-3">
            <h2 className="case-study-section-title has-text-weight-medium has-text-purple mb-6">
              The Solution
            </h2>

            <TextSection
              html={primaryText}
              className="mb-4"
              removeInlineStyles={removeInlineStyles}
            />
            {children}
            <TextSection
              html={secondaryText}
              className="case-study-solution-text-2 px-5"
              removeInlineStyles={removeInlineStyles}
            />
          </div>
        </div>
      </div>
    )
  }
)

// Animation constants
const ANIMATION = {
  START_STAGGER_MS: 160,
  DURATION_MIN_MS: 600,
  DURATION_MAX_MS: 2000,
  ACTIVATION_OFFSET_MS: 120,
  OBSERVER_DELAY_MS: 100,
  SETUP_DELAY_MS: 50,
  RETRY_DELAY_MS: 50,
}

// Helper to clear all timeouts
const clearTimeouts = timeouts => {
  timeouts.forEach(id => window.clearTimeout(id))
  timeouts.length = 0
}

export const CaseStudyResultsSection = ({
  primaryText,
  secondaryText,
  removeInlineStyles,
  metrics = [],
}) => {
  const safeMetrics = Array.isArray(metrics) ? metrics : []
  const hasMetrics = safeMetrics.length > 0
  const canAnimate = typeof window !== "undefined"

  const cardsContainerRef = useRef(null)
  const [isInView, setIsInView] = useState(false)
  const animationTimeoutsRef = useRef([])
  const hasAnimatedRef = useRef(false)
  const [visibleCount, setVisibleCount] = useState(0)
  const [activeCount, setActiveCount] = useState(0)
  const observerRef = useRef(null)

  // Setup intersection observer
  useEffect(() => {
    if (!hasMetrics || hasAnimatedRef.current) {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      return
    }

    if (
      typeof window === "undefined" ||
      typeof IntersectionObserver === "undefined"
    ) {
      setIsInView(true)
      return
    }

    let retryTimer = null

    const setupObserver = () => {
      if (!cardsContainerRef.current || hasAnimatedRef.current) {
        if (!hasAnimatedRef.current) {
          retryTimer = window.setTimeout(
            setupObserver,
            ANIMATION.RETRY_DELAY_MS
          )
        }
        return
      }

      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      const observer = new IntersectionObserver(
        entries => {
          if (!hasAnimatedRef.current && entries.some(e => e.isIntersecting)) {
            setIsInView(true)
          }
        },
        { threshold: 0.1, rootMargin: "0px" }
      )

      observer.observe(cardsContainerRef.current)
      observerRef.current = observer
    }

    const delayTimer = window.setTimeout(
      setupObserver,
      ANIMATION.OBSERVER_DELAY_MS
    )

    return () => {
      if (retryTimer) window.clearTimeout(retryTimer)
      if (delayTimer) window.clearTimeout(delayTimer)
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [hasMetrics, safeMetrics.length])

  // Animate cards when in view
  useEffect(() => {
    const totalCards = safeMetrics.length

    if (!hasMetrics) {
      setVisibleCount(0)
      setActiveCount(0)
      return
    }

    if (hasAnimatedRef.current || !canAnimate) {
      setVisibleCount(totalCards)
      setActiveCount(totalCards)
      if (!canAnimate) hasAnimatedRef.current = true
      return
    }

    if (!isInView) return

    setVisibleCount(0)
    setActiveCount(0)
    clearTimeouts(animationTimeoutsRef.current)

    const initDelay = window.setTimeout(() => {
      for (let index = 0; index < totalCards; index++) {
        const staggerDelay = index * ANIMATION.START_STAGGER_MS

        const visibilityTimeout = window.setTimeout(
          () => setVisibleCount(prev => Math.max(prev, index + 1)),
          staggerDelay
        )

        const activationTimeout = window.setTimeout(() => {
          setActiveCount(prev => {
            const newCount = Math.max(prev, index + 1)
            if (newCount === totalCards) {
              hasAnimatedRef.current = true
              setVisibleCount(totalCards)
              setActiveCount(totalCards)
              if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
              }
            }
            return newCount
          })
        }, staggerDelay + ANIMATION.ACTIVATION_OFFSET_MS)

        animationTimeoutsRef.current.push(visibilityTimeout, activationTimeout)
      }
    }, ANIMATION.SETUP_DELAY_MS)

    animationTimeoutsRef.current.push(initDelay)

    return () => clearTimeouts(animationTimeoutsRef.current)
  }, [canAnimate, hasMetrics, safeMetrics.length, isInView])

  if (!primaryText && !secondaryText && !hasMetrics) {
    return null
  }

  return (
    <div className="case-study-container">
      <div className="container py-6">
        <h2 className="case-study-section-title has-text-weight-medium has-text-purple mb-6">
          The Results
        </h2>

        <TextSection
          html={primaryText}
          removeInlineStyles={removeInlineStyles}
        />

        {hasMetrics && (
          <div
            className="case-study-results-cards py-5"
            ref={cardsContainerRef}
          >
            {safeMetrics.map((metric, index) => (
              <AnimatedMetricCard
                key={metric.index ?? index}
                metric={metric}
                index={index}
                isVisible={index < visibleCount}
                isActive={index < activeCount}
                canAnimate={canAnimate}
                startStaggerMs={ANIMATION.START_STAGGER_MS}
              />
            ))}
          </div>
        )}

        <TextSection
          html={secondaryText}
          className="mt-5"
          removeInlineStyles={removeInlineStyles}
        />
      </div>
    </div>
  )
}

// Parse metric value and prepare animation data
const parseMetricValue = metricValue => {
  const raw = `${metricValue}`.trim()
  if (!raw) {
    return {
      shouldAnimate: false,
      rawDisplay: "",
      initialDisplay: "0",
      finalDisplay: "",
    }
  }

  const numberMatch = raw.match(/-?\d[\d.,]*/g)
  const primaryMatch = numberMatch?.[0]
  if (!primaryMatch) {
    return {
      shouldAnimate: false,
      rawDisplay: raw,
      initialDisplay: raw,
      finalDisplay: raw,
    }
  }

  const matchIndex = raw.indexOf(primaryMatch)
  const prefixText = raw.slice(0, matchIndex)
  const suffixText = raw.slice(matchIndex + primaryMatch.length)
  const normalized = primaryMatch.replace(/[,\s]/g, "")
  const numericValue = Number(normalized)

  if (!Number.isFinite(numericValue)) {
    return {
      shouldAnimate: false,
      rawDisplay: raw,
      initialDisplay: raw,
      finalDisplay: raw,
    }
  }

  const decimalPart = normalized.includes(".")
    ? normalized.split(".")[1].length
    : 0
  const numberFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimalPart,
    maximumFractionDigits: decimalPart,
  })

  const formatNumber = value => {
    const fixedValue =
      decimalPart > 0 ? Number(value.toFixed(decimalPart)) : Math.round(value)
    return numberFormatter.format(fixedValue)
  }

  return {
    shouldAnimate: true,
    prefix: prefixText,
    suffix: suffixText,
    decimals: decimalPart,
    targetValue: numericValue,
    formatter: { format: formatNumber },
    initialDisplay: `${prefixText}${formatNumber(0)}${suffixText}`,
    finalDisplay: `${prefixText}${formatNumber(numericValue)}${suffixText}`,
    rawDisplay: raw,
  }
}

const AnimatedMetricCard = ({
  metric,
  index,
  isVisible,
  isActive,
  canAnimate,
  startStaggerMs,
}) => {
  const metricValue = metric?.value ?? ""
  const animationData = useMemo(() => parseMetricValue(metricValue), [
    metricValue,
  ])
  const {
    shouldAnimate,
    prefix,
    suffix,
    decimals,
    targetValue,
    formatter,
    initialDisplay,
    finalDisplay,
    rawDisplay,
  } = animationData

  const [displayValue, setDisplayValue] = useState(
    shouldAnimate ? initialDisplay : rawDisplay
  )
  const animationFrameRef = useRef(null)
  const animationStartRef = useRef(null)
  const hasAnimatedRef = useRef(false)

  // Reset animation state
  useEffect(() => {
    if (hasAnimatedRef.current) return
    setDisplayValue(shouldAnimate ? initialDisplay : rawDisplay)
    hasAnimatedRef.current = false
    animationStartRef.current = null
  }, [initialDisplay, rawDisplay, shouldAnimate])

  // Handle animation
  useEffect(() => {
    const cancelAnimation = () => {
      if (animationFrameRef.current && window?.cancelAnimationFrame) {
        window.cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }

    const resetToInitial = () => {
      cancelAnimation()
      setDisplayValue(shouldAnimate ? initialDisplay : rawDisplay)
      if (!isVisible) {
        hasAnimatedRef.current = false
        animationStartRef.current = null
      }
    }

    // Handle visibility/active states
    if (!isVisible || !isActive) {
      if (hasAnimatedRef.current) {
        setDisplayValue(finalDisplay)
      } else {
        resetToInitial()
      }
      return
    }

    if (!shouldAnimate || hasAnimatedRef.current) {
      if (!shouldAnimate) setDisplayValue(rawDisplay)
      return
    }

    if (!canAnimate || typeof window === "undefined") {
      setDisplayValue(finalDisplay)
      hasAnimatedRef.current = true
      return
    }

    // Calculate animation duration
    const baseDuration =
      targetValue === 0
        ? 0
        : Math.min(
            ANIMATION.DURATION_MAX_MS,
            Math.max(900, Math.abs(targetValue) * 32)
          )
    const staggerDelay = (startStaggerMs ?? ANIMATION.START_STAGGER_MS) * index
    const duration = Math.max(
      ANIMATION.DURATION_MIN_MS,
      Math.min(ANIMATION.DURATION_MAX_MS, baseDuration - staggerDelay * 0.4)
    )

    // Animation step function
    const step = timestamp => {
      if (!animationStartRef.current) animationStartRef.current = timestamp

      const elapsed = timestamp - animationStartRef.current
      const progress = duration === 0 ? 1 : Math.min(elapsed / duration, 1)
      const easedProgress = 1 - Math.pow(1 - progress, 3)
      const rawValue = targetValue * easedProgress
      const clampedValue =
        targetValue >= 0
          ? Math.min(rawValue, targetValue)
          : Math.max(rawValue, targetValue)

      const formattedValue = formatter.format(
        decimals > 0
          ? Number(clampedValue.toFixed(decimals))
          : Math.round(clampedValue)
      )

      setDisplayValue(`${prefix ?? ""}${formattedValue}${suffix ?? ""}`)

      if (progress < 1) {
        animationFrameRef.current = window.requestAnimationFrame(step)
      } else {
        setDisplayValue(finalDisplay)
        hasAnimatedRef.current = true
      }
    }

    animationStartRef.current = null
    animationFrameRef.current = window.requestAnimationFrame(step)

    return cancelAnimation
  }, [
    canAnimate,
    decimals,
    finalDisplay,
    formatter,
    index,
    initialDisplay,
    isActive,
    isVisible,
    prefix,
    rawDisplay,
    shouldAnimate,
    suffix,
    targetValue,
    startStaggerMs,
  ])

  const arrowIcon =
    metric.arrowDirection === "up"
      ? faArrowUp
      : metric.arrowDirection === "down"
      ? faArrowDown
      : null

  return (
    <div className={`case-study-result-card${isVisible ? " is-visible" : ""}`}>
      <div className="case-study-result-card-content">
        <div className="case-study-result-card-value">
          <span className="case-study-result-card-number">{displayValue}</span>
          {arrowIcon && (
            <FontAwesomeIcon
              icon={arrowIcon}
              className="case-study-result-card-arrow"
            />
          )}
        </div>
        <p className="case-study-result-card-label">{metric.label}</p>
      </div>
    </div>
  )
}
export const CaseStudyExploreSection = React.memo(
  ({ slug, title = "Explore More Case Studies", limit = 3, ignoreSlugs }) => {
    const slugsToIgnore = useMemo(
      () => (slug ? [slug, ...(ignoreSlugs || [])] : ignoreSlugs || []),
      [slug, ignoreSlugs]
    )

    return (
      <div className="container mb-6 explore-case-studies-wrapper home-case-studies mt-6">
        <h2 className="case-study-section-title has-text-weight-medium has-text-purple mb-6">
          {title}
        </h2>
        <div className="explore-case-studies-grid">
          <CaseStudiesSection
            limit={limit}
            ignoreSlugs={slugsToIgnore}
            logoContext={LOGO_PLACEMENT.homepageCaseStudyCards}
          />
        </div>
      </div>
    )
  }
)
